import React, { forwardRef, useImperativeHandle } from "react";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Typography from "@material-ui/core/Typography";
import { CloudUpload } from "@material-ui/icons";
import { useDropzone } from "react-dropzone";
import UiButton from "./UiButton";

const styles = (theme: Theme) =>
  createStyles({
    fieldLabel: {
      display: "block",
      fontSize: 16,
      fontWeight: 200,
    },
    dropContainer: {
      border: `dashed 1px ${theme.palette.grey[400]}`,
      marginTop: theme.spacing.unit,
      padding: 0,
      minHeight: 125,
      borderRadius: 3,
      color: theme.palette.text.secondary,
      outline: "none",
    },
    dropContainerNoMargin: {
      marginTop: 0,
    },
    dropContainerDragActive: {
      backgroundColor: fade(theme.palette.primary.main, 0.05),
      borderColor: theme.palette.primary.main,
    },
    dropContainerCompact: {
      minHeight: 44,
    },
    dropContainerCompactLoading: {
      minHeight: 56,
    },
    loadingWrapper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: `${theme.spacing.unit}px`,
      width: "100%",
    },
    loadingWrapperCompact: {
      gap: `${theme.spacing.unit / 2}px`,
    },
    dropZoneContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: theme.spacing.unit * 3,
      width: "100%",
      boxSizing: "border-box",
    },
    dropZoneIcon: {
      fontSize: 48,
      color: theme.palette.grey[500],
      marginBottom: theme.spacing.unit,
    },
    dropZoneText: {
      margin: 0,
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.6,
    },
    dropZoneContentCompact: {
      flexDirection: "row",
      justifyContent: "center",
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
      gap: `${theme.spacing.unit / 2}px`,
      alignItems: "center",
    },
    dropZoneIconCompact: {
      fontSize: 22,
      marginBottom: 6,
    },
    dropZoneTextCompact: {
      fontSize: 14,
      lineHeight: 1.4,
    },
    uploadProperties: {
      color: theme.palette.text.secondary,
      textAlign: "center",
      marginTop: theme.spacing.unit,
      marginBottom: theme.spacing.unit,
      fontSize: 14,
      fontWeight: 200,
    },
    metaText: {
      marginTop: theme.spacing.unit,
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    errorText: {
      marginTop: theme.spacing.unit,
    },
  });

export interface UiFileDropzoneHandle {
  open: () => void;
}

export interface UiFileDropzoneProps {
  accept: string;
  multiple?: boolean;
  disabled?: boolean;
  onDrop: (files: File[]) => void;
  fieldLabel?: string;
  fieldRequired?: boolean;
  dragActiveText?: string;
  idlePrefix?: string;
  chooseButtonLabel?: string;
  selectedFileName?: string | null;
  emptyZoneText?: string;
  showIcon?: boolean;
  footer?: React.ReactNode;
  loading?: boolean;
  loadingContent?: React.ReactNode;
  error?: string | null;
  metaText?: string | null;
  className?: string;
  noTopMargin?: boolean;
  variant?: "default" | "compact";
  classes: any;
}

const UiFileDropzone = forwardRef<UiFileDropzoneHandle, UiFileDropzoneProps>(
  (props, ref) => {
    const {
      accept,
      multiple = false,
      disabled = false,
      onDrop,
      fieldLabel,
      fieldRequired = false,
      dragActiveText = "Déposez vos fichiers ici",
      idlePrefix = "Glisser-déposer vos fichiers ou",
      chooseButtonLabel = "choisir des fichiers",
      selectedFileName = null,
      emptyZoneText,
      showIcon = true,
      footer,
      loading = false,
      loadingContent,
      error = null,
      metaText = null,
      className,
      noTopMargin = false,
      variant = "default",
      classes,
    } = props;

    const isCompact = variant === "compact";

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      accept,
      multiple,
      disabled: disabled || loading,
      onDrop,
    });

    useImperativeHandle(ref, () => ({ open }));

    const showChooseButton = Boolean(idlePrefix) && !selectedFileName && !emptyZoneText;

    return (
      <div className={className}>
        {fieldLabel && (
          <span className={classes.fieldLabel}>
            {fieldLabel}
            {fieldRequired && (
              <span>
                {" "}
                <sup>*</sup>
              </span>
            )}
          </span>
        )}
        <div
          {...getRootProps()}
          className={classNames(classes.dropContainer, {
            [classes.dropContainerNoMargin]: noTopMargin,
            [classes.dropContainerDragActive]: isDragActive,
            [classes.dropContainerCompact]: isCompact,
            [classes.dropContainerCompactLoading]: isCompact && loading,
          })}
        >
          <input {...getInputProps()} />
          <div
            className={classNames(classes.dropZoneContent, {
              [classes.dropZoneContentCompact]: isCompact,
            })}
          >
            {loading && loadingContent ? (
              <div
                className={classNames(classes.loadingWrapper, {
                  [classes.loadingWrapperCompact]: isCompact,
                })}
              >
                {loadingContent}
              </div>
            ) : (
              <>
                {showIcon && (
                  <CloudUpload
                    className={classNames(classes.dropZoneIcon, {
                      [classes.dropZoneIconCompact]: isCompact,
                    })}
                  />
                )}
                <p
                  className={classNames(classes.dropZoneText, {
                    [classes.dropZoneTextCompact]: isCompact,
                  })}
                >
                  {isDragActive ? (
                    dragActiveText
                  ) : selectedFileName ? (
                    selectedFileName
                  ) : emptyZoneText && !idlePrefix ? (
                    emptyZoneText
                  ) : isCompact ? (
                    <>
                      {idlePrefix || "Glisser-déposer ou"}{" "}
                      <UiButton
                        type="button"
                        variant="text"
                        color="primary"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          open();
                        }}
                        disabled={disabled}
                      >
                        {chooseButtonLabel}
                      </UiButton>
                    </>
                  ) : (
                    <>
                      {idlePrefix}{" "}
                      {showChooseButton && (
                        <UiButton
                          type="button"
                          variant="text"
                          color="primary"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            open();
                          }}
                          disabled={disabled}
                        >
                          {chooseButtonLabel}
                        </UiButton>
                      )}
                    </>
                  )}
                </p>
                {!isDragActive && !isCompact && footer && (
                  <div className={classes.uploadProperties}>{footer}</div>
                )}
              </>
            )}
          </div>
        </div>
        {metaText && (
          <Typography variant="body2" className={classes.metaText}>
            {metaText}
          </Typography>
        )}
        {error && (
          <Typography
            color="error"
            variant="body2"
            className={classes.errorText}
          >
            {error}
          </Typography>
        )}
      </div>
    );
  }
);

UiFileDropzone.displayName = "UiFileDropzone";

export default withStyles(styles)(UiFileDropzone);
